import type { FuseOptionKey, FuseOptionKeyObject, FuseResult, IFuseOptions } from "fuse.js"

export function allArrayElementsEqual<T>(a: T[], b: T[], property?: string): boolean {
  if (a.length !== b.length) {
    return false
  }

  // If dealing with nested identifiers (property names), extract the strings into new arrays.
  if (property) {
    a = a.map((item) => _getNestedProperty(item, property))
    b = b.map((item) => _getNestedProperty(item, property))
  }

  const sortedA = a.sort()
  const sortedB = b.sort()

  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) {
      return false
    }
  }

  return true
}

/**
 * Returns each item once, using its id as the unique key.
 * When an id occurs in multiple source collections, the first occurrence wins.
 */
export function uniqueById<T extends { id?: string }>(items: T[]): T[] {
  const seenIds = new Set<string>()
  return items.filter((item) => {
    if (!item.id || seenIds.has(item.id)) {
      return false
    }
    seenIds.add(item.id)
    return true
  })
}

function _getNestedProperty(obj: any, propertyPath: string) {
  return propertyPath.split(".").reduce((acc, prop) => {
    return acc && acc[prop]
  }, obj)
}

import { deburr } from "lodash-es"
import Fuse from "fuse.js"

type SearchCollectionOptions = {
  // If true, if all words in the search text are found in any field, the item is included
  thorough?: boolean
  // Stop searching once this number of results is reached. Pass null to disable limit.
  maxResults?: number | null
  // Stop searching once the average distance of all search words is greater than this value.
  // Set to 0 to disable fuzzy search.
  maxAverageDistance?: number
}

/**
 * Perform a search on a collection using a list of fields.
 * @deprecated Use fuzzySearchCollection instead.
 * @param collection
 * @param searchText
 * @param getFields
 * @param options
 */
export function searchCollection<T>(
  collection: T[],
  searchText: string | undefined,
  getFields: (item: T) => (string | undefined)[],
  options: SearchCollectionOptions = {}
) {
  // TODO max results option if performance is a concern
  // Could be implemented by slicing the collection and searching slice by slice
  // until the max results are found.

  const { thorough = false, maxResults = 20, maxAverageDistance = 0.5 } = options

  // If the search text is undefined and maxResults is null, return the (entire or sliced) collection without searching.
  if (!searchText) {
    if (maxResults === null) {
      // Return the entire collection if searchText is undefined and maxResults is explicitly null
      return collection
    }
    return collection.slice(0, maxResults) // Return only up to maxResults items if searchText is undefined
  }

  // Normalize the search text to lowercase and remove diacritics
  const normalizedSearchText = _normalizeSearchString(searchText)
  const searchWords = thorough
    ? normalizedSearchText.split(/\s+/).filter((word) => word.length > 0)
    : [normalizedSearchText]

  // Prepare a list of single search strings by normalizing and joining each field.
  const haystack = []
  for (const item of collection) {
    // Normalize each field, lowercasing and removing diacritics
    const fields = getFields(item).map((field) => (field ? _normalizeSearchString(field) : ""))

    // Combine all fields into a single string
    const haybale = fields.join(" ")

    haystack.push({ item, haybale })
  }

  // We consider two types of results: exact matches and fuzzy matches.
  const exactResultsSet = new Set<T>()
  const fuzzyResults: { item: T; distance: number }[] = []

  // Search for exact matches first.
  let resultCount = 0
  for (const { item, haybale } of haystack) {
    if (maxResults && resultCount >= maxResults) {
      break // Stop searching once the maximum number of results is reached
    }
    // Consider the item an exact match if all search words are found in the combined fields.
    if (searchWords.every((word) => haybale.includes(word))) {
      resultCount++
      exactResultsSet.add(item)
    }
  }

  if (maxAverageDistance > 0) {
    // Search for fuzzy matches next.
    for (const { item, haybale } of haystack) {
      if (maxResults && resultCount >= maxResults) {
        break // Stop searching once the maximum number of results is reached
      }

      if (!exactResultsSet.has(item)) {
        // Skip the item if it was already found as an exact match
        const distance = searchWords.reduce(
          (acc, word) => acc + _smallestDistance(haybale, word),
          0
        )
        const averageDistance = distance / searchWords.length
        if (averageDistance <= maxAverageDistance) {
          resultCount++
          fuzzyResults.push({ item, distance })
        }
      }
    }
  }

  // Sort fuzzy results by distance, then map to items only
  const sortedFuzzyResults = fuzzyResults
    .sort((a, b) => a.distance - b.distance)
    .map((result) => result.item)

  return Array.from(exactResultsSet).concat(sortedFuzzyResults)
}

function _smallestDistance(haystack: string, needle: string): number {
  let smallestDistance = Infinity
  for (let start = 0; start < haystack.length - needle.length + 1; start++) {
    // Grab substring of the same length as needle
    const substr = haystack.substring(start, start + needle.length)
    const distance = _levenshteinDistance(substr, needle)
    if (distance < smallestDistance) {
      smallestDistance = distance
    }
  }
  return smallestDistance
}

function _levenshteinDistance(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0))

  for (let i = 0; i <= a.length; i++) {
    dp[i][0] = i
  }
  for (let j = 0; j <= b.length; j++) {
    dp[0][j] = j
  }

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1, // deletion
        dp[i][j - 1] + 1, // insertion
        dp[i - 1][j - 1] + cost // substitution
      )
    }
  }

  return dp[a.length][b.length]
}

/**
 * Find the index of an item in a collection using binary search.
 */
export function findIndexByBinarySearch<T>(
  collection: T[],
  getField: (item: T) => number,
  number: number
): number {
  let low = 0
  let high = collection.length

  while (low < high) {
    const mid = Math.floor((low + high) / 2)
    if (getField(collection[mid]) < number) {
      low = mid + 1
    } else {
      high = mid
    }
  }

  return low
}

/**
 * Perform a fuzzy multi-word AND search on a collection using Fuse.js.
 * @param collection The collection to search.
 * @param fuseOptionsOrFuseOptionKeys The Fuse.js options or the keys to search on. In the latter case, a predefined
 * Fuse.js options configuration will be used.
 * @param searchString The search string.
 * @param returnRawResults If true, returns the raw Fuse.js results instead of just the items.
 * @returns The search results.
 *
 */
export function fuzzySearchCollection<T>(
  collection: T[],
  fuseOptionsOrFuseOptionKeys: IFuseOptions<T> | FuseOptionKey<T>[],
  searchString: string,
  returnRawResults: true
): FuseResult<T>[]
export function fuzzySearchCollection<T>(
  collection: T[],
  fuseOptionsOrFuseOptionKeys: IFuseOptions<T> | FuseOptionKey<T>[],
  searchString: string,
  returnRawResults?: false
): T[]
export function fuzzySearchCollection<T>(
  collection: T[],
  fuseOptionsOrFuseOptionKeys: IFuseOptions<T> | FuseOptionKey<T>[],
  searchString: string,
  returnRawResults = false
): T[] | FuseResult<T>[] {
  const fuseOptions = prepareFuseOptions(fuseOptionsOrFuseOptionKeys)

  // Normalize search string: remove diacritics, lowercase, and remove dots/punctuation
  const normalizedSearchString = searchString ? _normalizeSearchString(searchString) : ""

  if (normalizedSearchString.length === 0) {
    return collection
  }

  const searchPattern = generateSearchPattern(fuseOptions.keys!, normalizedSearchString)
  const fuse = new Fuse(collection, fuseOptions)
  const results = fuse.search(searchPattern)
  return returnRawResults ? results : results.map((result) => result.item)
}

/**
 * Prepare the Fuse.js options for searching. If the keys are provided, they will be added to the default options.
 * If the options are provided, they will be used directly.
 * @param fuseOptionsOrFuseOptionKeys The Fuse.js options or the keys to search on.
 */
export function prepareFuseOptions<T>(
  fuseOptionsOrFuseOptionKeys: IFuseOptions<T> | FuseOptionKey<T>[]
) {
  const defaultFuseOptions: IFuseOptions<T> = {
    includeScore: true,
    threshold: 0.3,
    ignoreLocation: true,
    findAllMatches: true,
    ignoreDiacritics: true,
    // Custom getFn to normalize values by removing dots and punctuation
    getFn: (obj: any, path: string | string[]) => {
      const value = Fuse.config.getFn(obj, path)
      if (typeof value === "string") {
        return _normalizeSearchString(value)
      }
      return value
    }
  }

  let fuseOptions: IFuseOptions<T>
  if (Array.isArray(fuseOptionsOrFuseOptionKeys)) {
    // If the keys are provided, use the default options and add the keys to them.
    fuseOptions = { ...defaultFuseOptions, keys: fuseOptionsOrFuseOptionKeys }
  } else {
    // If the options are provided, use them directly.
    fuseOptions = fuseOptionsOrFuseOptionKeys // TODO merge rather than overwrite
  }

  if (!fuseOptions.keys) {
    throw new Error("No keys provided to fuseOptions")
  }

  return fuseOptions
}

/**
 * Generate a search pattern for the Fuse.js search that enables AND logic for multiple search terms.
 * @param fuseOptionKeys The keys to search on.
 * @param searchString The normalized search string.
 *
 * @see https://github.com/krisk/Fuse/issues/235#issuecomment-850269634
 */
function generateSearchPattern<T>(fuseOptionKeys: Array<FuseOptionKey<T>>, searchString: string) {
  // Tokenise the search string by spaces, ignoring spaces within "quoted text".
  const tokenisedSearchQuery = tokeniseStringWithQuotesBySpaces(searchString)

  // Extract field names from fuseOptions keys.
  const fields = fuseOptionKeys.reduce<FuseOptionKey<T>[]>((acc, key) => {
    const fieldName = (key as FuseOptionKeyObject<T>).name
    if (!acc.includes(fieldName)) {
      acc.push(fieldName)
    }
    return acc
  }, [])

  // Build and return the dynamic search pattern.
  return {
    $and: tokenisedSearchQuery.map((token) => ({
      $or: fields.map((field: any) => ({ [field]: token }))
    }))
  }
}

/**
 * Split the provided string by spaces (ignoring spaces within "quoted text") into an array of tokens.
 * @see https://stackoverflow.com/a/16261693/1265472
 *
 */
function tokeniseStringWithQuotesBySpaces(string: string): string[] {
  return string.match(/("[^"]*?"|[^"\s]+)+(?=\s*|\s*$)/g) ?? []
}

/**
 * EXPERIMENTAL
 * Perform a fuzzy search on a collection with two-tier matching:
 * 1. Best matches: Items matching all words in the full normalized search string (regular fuzzySearchCollection)
 * 2. Second-order matches: Items matching one or more individual words from the search string
 *
 * @param collection The collection to search.
 * @param fuseOptionsOrFuseOptionKeys The Fuse.js options or the keys to search on.
 * @param searchString The search string (will be normalized with deburr and lowercased).
 * @returns An array with best matches first, followed by second-order matches (no duplicates).
 */
export function fuzzySearchCollectionMultiOrder<T>(
  collection: T[],
  fuseOptionsOrFuseOptionKeys: IFuseOptions<T> | FuseOptionKey<T>[],
  searchString: string
): T[] {
  const maxSecondOrderMatches = 5
  const maxSecondOrderScore = 0.1 // Maximum score for second-order matches. 0 is perfect match, 1 is worst match.

  const normalizedSearchText = _normalizeSearchString(searchString)

  // Get best matches using the full search string
  const bestMatches = fuzzySearchCollection(
    collection,
    fuseOptionsOrFuseOptionKeys,
    normalizedSearchText,
    false
  )

  // Store normal Fuzzy search results
  const bestMatchesSet = new Set<T>(bestMatches)

  // Split search string into individual words
  const searchWords = normalizedSearchText.split(/\s+/).filter((word) => word.length > 0)

  // Search for each word individually and collect results by refIndex
  const resultsByRefIndex = new Map<number, any>()

  for (const word of searchWords) {
    const results = fuzzySearchCollection(collection, fuseOptionsOrFuseOptionKeys, word, true)

    for (const result of results) {
      const refIndex = result.refIndex
      const score = result.score ?? 0

      // Only keep the result with the highest score (lowest score value in Fuse.js) for each refIndex
      if (
        !resultsByRefIndex.has(refIndex) ||
        (resultsByRefIndex.get(refIndex).score ?? 0) > score
      ) {
        resultsByRefIndex.set(refIndex, result)
      }
    }
  }

  // Extract items from the best scoring results, excluding those already in best matches
  // Sort by score (lower is better in Fuse.js, so sort ascending)
  const secondOrderResults = Array.from(resultsByRefIndex.values())
    .filter((result) => !bestMatchesSet.has(result.item))
    .filter((result) => (result.score ?? 1) <= maxSecondOrderScore)
    .sort((a, b) => (a.score ?? 1) - (b.score ?? 1))

  const secondOrderMatches = secondOrderResults.map((result) => result.item)

  return [...bestMatches, ...secondOrderMatches.slice(0, maxSecondOrderMatches)]
}

function _normalizeSearchString(searchString: string): string {
  // Normalize: remove diacritics, lowercase, remove dots/punctuation
  return deburr(searchString.trim().toLowerCase())
    .replace(/[.\s]+/g, " ")
    .trim()
}
